import React, { useState, useEffect } from 'react';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showHttpsWarning, setShowHttpsWarning] = useState(false);

  useEffect(() => {
    const isMyStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsStandalone(isMyStandalone);

    const userAgent = window.navigator.userAgent.toLowerCase();
    const isAppleMobile = /iphone|ipad|ipod/.test(userAgent);
    const isMobileDevice = /android|iphone|ipad|ipod|mobi|mini|tablet|iemobile/.test(userAgent);
    setIsIOS(isAppleMobile);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      triggerShowPromptDelayed();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const handleAppInstalled = () => {
      console.log('🎉 Cibill PWA was installed successfully!');
      setDeferredPrompt(null);
      setShowPrompt(false);
      setShowIOSInstructions(false);
      setShowHttpsWarning(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    const triggerShowPromptDelayed = () => {
      const lastDismissed = localStorage.getItem('pwa_prompt_dismissed');
      const now = Date.now();
      const showPromptCooldown = 7 * 24 * 60 * 60 * 1000;

      if (!lastDismissed || (now - parseInt(lastDismissed, 10) > showPromptCooldown)) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 1500);
      }
    };

    if (isMobileDevice && !isMyStandalone) {
      triggerShowPromptDelayed();
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = () => {
    if (isIOS) {
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the PWA install prompt');
          setDeferredPrompt(null);
          setShowPrompt(false);
        } else {
          console.log('User dismissed the PWA install prompt');
        }
      });
    } else {
      setShowHttpsWarning(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
    setShowPrompt(false);
    setShowIOSInstructions(false);
    setShowHttpsWarning(false);
  };

  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="pwa-install-container">
      {/* 1. Main Install Banner */}
      {!showIOSInstructions && !showHttpsWarning && (
        <div className="pwa-install-banner">
          <div className="pwa-logo-content">
            <div className="pwa-app-icon-wrapper">
              <img src="/favicon.png" alt="Cibill CRM Logo" className="pwa-app-icon" />
            </div>
            <div className="pwa-text-details">
              <h5 className="pwa-title">Cibill CRM Dashboard</h5>
              <p className="pwa-subtitle">Install the app on your home screen for quick, offline access.</p>
            </div>
          </div>
          <div className="pwa-action-buttons">
            <button className="pwa-btn pwa-btn-dismiss" onClick={handleDismiss}>
              Not Now
            </button>
            <button className="pwa-btn pwa-btn-install" onClick={handleInstallClick}>
              Install App
            </button>
          </div>
          <button className="pwa-close-x" onClick={handleDismiss} aria-label="Close prompt">
            &times;
          </button>
        </div>
      )}

      {showIOSInstructions && (
        <div className="pwa-ios-modal">
          <div className="pwa-ios-header">
            <img src="/logo192.png" alt="Cibill CRM Logo" className="pwa-ios-icon" />
            <div>
              <h5 className="pwa-ios-title">Add to Home Screen</h5>
              <p className="pwa-ios-subtitle">Cibill Construction Management</p>
            </div>
            <button className="pwa-ios-close" onClick={handleDismiss}>&times;</button>
          </div>

          <div className="pwa-ios-body">
            <p className="pwa-ios-intro">Install Cibill CRM on your iOS Device:</p>
            <ol className="pwa-ios-steps">
              <li>
                Tap Safari's <strong>Share</strong> button in the bottom menu bar:
                <div className="pwa-ios-step-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#007aff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </div>
              </li>
              <li>
                Scroll down the share list and select <strong>Add to Home Screen</strong>:
                <div className="pwa-ios-step-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                </div>
              </li>
              <li>
                Tap <strong>Add</strong> in the top right corner.
              </li>
            </ol>
          </div>

          <div className="pwa-ios-footer">
            <button className="pwa-btn pwa-btn-ios-close" onClick={handleDismiss}>
              Got it
            </button>
          </div>
        </div>
      )}

      {showHttpsWarning && (
        <div className="pwa-ios-modal pwa-android-modal">
          <div className="pwa-ios-header">
            <img src="/logo192.png" alt="Cibill CRM Logo" className="pwa-ios-icon" />
            <div>
              <h5 className="pwa-ios-title">One-Click Install Blocked</h5>
              <p className="pwa-ios-subtitle">Cibill Construction Management</p>
            </div>
            <button className="pwa-ios-close" onClick={handleDismiss}>&times;</button>
          </div>

          <div className="pwa-ios-body">
            <p className="pwa-ios-intro" style={{ color: '#d9534f', fontWeight: 'bold' }}>
              🔒 HTTPS Connection Required
            </p>
            <p className="pwa-ios-explanation">
              For security, mobile browsers strictly prevent programmatic "One-Click Install" dialogues on unencrypted Web connections (HTTP).
            </p>
            <hr />
            <p style={{ fontSize: '12px' }}>
              <strong>For Production:</strong> Deploying the PWA to an HTTPS address will automatically enable instant direct installation.
            </p>
            <p style={{ fontSize: '12px' }}>
              <strong>For Local Testing (Add Manually):</strong> Open Chrome's menu (3 dots) & select <strong>Add to Home screen</strong>.
            </p>
          </div>

          <div className="pwa-ios-footer">
            <button className="pwa-btn pwa-btn-ios-close" onClick={handleDismiss}>
              I Understand
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PWAInstallPrompt;
